/* Notification Center — Permit Library source of truth (demo).
 *
 * The Targets tab of a Notification Rule does NOT re-enter limit values — it
 * reads them live from the Permit Library, exactly as the running app will
 * once the reader hook lands.  This module mirrors the REAL Permit Library API
 * shape 1:1 so that swap is a drop-in:
 *
 *   • PlPermit            ≈ GET /api/v1/permit-libraries        (dbo.PermitLibrary)
 *       permitName, permitType (the "Scope"), sampleMatrix
 *   • PlLimitRow          ≈ GET /api/v1/permit-setup/by-permit/{id}
 *                            → PermitSetupDetail  (dbo.Permitsetup)
 *       parameterName, parameterSynonym, limitTypeName, operatorDisplay,
 *       permit (Limit1), permit2 (Limit2), unitsName, effectiveFrom/To
 *
 * OPERATORS are the SYMBOL form the API returns in OperatorDisplay
 * ("<=", ">=", "Between", "Is") — see PermitSetupController.Operators — never a
 * mathematical glyph, so the Targets grid reads identically to the Permit
 * Limits child grid on the Permit Library detail view.
 *
 * The seed values are transcribed from the approved Permit-Library prototype
 * (ALPACA-Permit-To-Notification-v1.2/3-Working-Prototype).  No limit is
 * fabricated in the Targets tab: everything shown there is derived from THIS
 * file via buildPermitTargets().  When the real reader lands it replaces this
 * file; the Targets component consumes the typed shapes either way. */

/** One per-parameter limit under a permit — mirrors PermitSetupDetail. */
export interface PlLimitRow {
  /** dbo.Parameter — the analyte this limit applies to. */
  parameter: string;
  /** dbo.Parameter.Synonym — read-only, blank when none recorded. */
  synonym?: string;
  /** dbo.AI_LimitType.LimitTypeName — MCL / SMCL / AL / MRL / … */
  limitType: string;
  /** OperatorDisplay symbol: "<=", ">=", "Between", "Is", "Is Not". */
  operator: string;
  /** Limit1 (Permit) — a threshold, or a word for qualitative limits ("Absent"). */
  limit: string;
  /** Limit2 (Permit2) — the upper bound for the "Between" operator only. */
  limit2?: string;
  /** dbo.Units.UnitsName. */
  units: string;
  /** yyyy-mm-dd — start of this limit's effective window (blank = always). */
  effectiveFrom?: string;
  /** yyyy-mm-dd — end of the window (blank = open-ended / current). */
  effectiveTo?: string;
}

/** One Permit Library entry — mirrors dbo.PermitLibrary. */
export interface PlPermit {
  permitId: string;
  /** dbo.PermitLibrary.PermitName — the "Permit Name" column. */
  permitName: string;
  /** dbo.PermitLibrary.PermitType — the "Scope – Permit Type" column. */
  permitType: string;
  /** dbo.PermitLibrary.SampleMatrix. */
  sampleMatrix: string;
  active: boolean;
  /** The permit's limit rows, flat, as by-permit/{id} returns them. */
  limits: PlLimitRow[];
}

/**
 * Demo Permit Library — the source of truth for the Targets tab.
 * Transcribed verbatim from the working prototype's `permits[]`.
 */
export const NC_PERMIT_LIBRARY: PlPermit[] = [
  {
    permitId: 'P001', permitName: 'Drinking Water Permit', permitType: 'Drinking Water',
    sampleMatrix: 'Drinking Water', active: true,
    limits: [
      { parameter: 'Arsenic (As)', synonym: 'As', limitType: 'MCL', operator: '<=', limit: '10', units: 'ug/L', effectiveFrom: '2026-01-01' },
      { parameter: 'Fluoride', limitType: 'MCL', operator: '<=', limit: '4.0', units: 'mg/L', effectiveFrom: '2026-01-01' },
      { parameter: 'Fluoride', limitType: 'SMCL', operator: '<=', limit: '2.0', units: 'mg/L', effectiveFrom: '2026-01-01' },
      { parameter: 'Lead (Pb)', synonym: 'Pb', limitType: 'AL', operator: '<=', limit: '15', units: 'ug/L', effectiveFrom: '2026-01-01', effectiveTo: '2027-10-31' },
      { parameter: 'Lead (Pb)', synonym: 'Pb', limitType: 'AL', operator: '<=', limit: '10', units: 'ug/L', effectiveFrom: '2027-11-01' },
      { parameter: 'Copper (Cu)', synonym: 'Cu', limitType: 'AL', operator: '<=', limit: '1300', units: 'ug/L', effectiveFrom: '2026-01-01' },
      { parameter: 'Nitrate as N', limitType: 'MCL', operator: '<=', limit: '10.0', units: 'mg/L', effectiveFrom: '2026-01-01' },
      { parameter: 'pH', limitType: 'SMCL', operator: 'Between', limit: '6.5', limit2: '8.5', units: 'SU' },
      { parameter: 'TTHM', limitType: 'MCL', operator: '<=', limit: '80', units: 'ug/L' },
      { parameter: 'HAA5', limitType: 'MCL', operator: '<=', limit: '60', units: 'ug/L' },
      { parameter: 'Benzene', limitType: 'MCL', operator: '<=', limit: '5', units: 'ug/L' },
      { parameter: 'Vinyl chloride', limitType: 'MCL', operator: '<=', limit: '2', units: 'ug/L' },
      { parameter: 'Trichloroethylene', limitType: 'MCL', operator: '<=', limit: '5', units: 'ug/L' },
      { parameter: 'Total Coliform', limitType: 'MCL', operator: 'Is', limit: 'Absent', units: 'P/A' },
      { parameter: 'E. coli', limitType: 'MCL', operator: 'Is', limit: 'Absent', units: 'P/A' },
      { parameter: 'Heterotrophic Plate Count', limitType: 'MCL', operator: '<=', limit: '500', units: 'CFU/mL' },
    ],
  },
  {
    permitId: 'P002', permitName: 'City WWTP NPDES Permit', permitType: 'NPDES',
    sampleMatrix: 'Wastewater', active: true,
    limits: [
      { parameter: 'BOD, 5-Day', limitType: 'Monthly Average', operator: '<=', limit: '30', units: 'mg/L' },
      { parameter: 'TSS', limitType: 'Monthly Average', operator: '<=', limit: '30', units: 'mg/L' },
      { parameter: 'pH', limitType: 'Minimum', operator: '>=', limit: '6.0', units: 'SU' },
      { parameter: 'pH', limitType: 'Maximum', operator: '<=', limit: '9.0', units: 'SU' },
    ],
  },
];

/**
 * One row in the Targets MAIN grid — a single parameter under one permit,
 * carrying ALL of that parameter's Permit Library limit rows for the sub-grid.
 * A parameter with several limit types (Fluoride: MCL + SMCL, Lead: two ALs
 * across effective windows) appears ONCE here; every limit is under `limits`.
 */
export interface PermitTarget {
  /** Stable per-row key: permitId + parameter. */
  key: string;
  parameter: string;
  synonym?: string;
  /** "Scope – Permit Type" (the owning permit's PermitType). */
  scope: string;
  /** "Permit Name" (the owning permit's PermitName). */
  permitName: string;
  /** Every configured limit for this parameter under this permit. */
  limits: PlLimitRow[];
}

/**
 * Collapse the Permit Library into Targets main-grid rows: one row per
 * (permit, parameter), sub-rows = that parameter's limits.  Ordered by permit,
 * then parameter name — the same order the by-permit reader returns, so the
 * grid reads predictably.  Never duplicates a parameter for its multiple limit
 * types.
 */
export function buildPermitTargets(library: PlPermit[] = NC_PERMIT_LIBRARY): PermitTarget[] {
  const out: PermitTarget[] = [];
  for (const permit of library) {
    if (!permit.active) continue;
    // Preserve first-seen parameter order within the permit while grouping.
    const byParam = new Map<string, PermitTarget>();
    for (const row of permit.limits) {
      const key = `${permit.permitId}::${row.parameter}`;
      let group = byParam.get(key);
      if (!group) {
        group = {
          key,
          parameter: row.parameter,
          synonym: row.synonym,
          scope: permit.permitType,
          permitName: permit.permitName,
          limits: [],
        };
        byParam.set(key, group);
        out.push(group);
      }
      // Keep a synonym if any limit row carries one.
      if (!group.synonym && row.synonym) group.synonym = row.synonym;
      group.limits.push(row);
    }
  }
  out.sort((a, b) => a.permitName.localeCompare(b.permitName) || a.parameter.localeCompare(b.parameter));
  return out;
}
